import { Scene } from './Scene';
import { createLogger } from '../Utils/Logger';
import { PerformanceMonitor } from '../Utils/PerformanceMonitor';
import { ServiceContainer } from '../Core/ServiceContainer';
const logger = createLogger('World');
/**
 * @zh World默认配置
 * @en World default configuration
 */
const DEFAULT_CONFIG = {
    name: 'World',
    debug: false,
    maxScenes: 10,
    autoCleanup: true,
    cleanupThresholdMs: 5 * 60 * 1000
};
/**
 * @zh World类 - ECS世界管理器
 * @en World class - ECS world manager
 *
 * @zh World是Scene的容器，每个World可以管理多个Scene。
 * World拥有独立的服务容器，用于管理World级别的全局服务。
 * @en World is a container for Scenes, each World can manage multiple Scenes.
 * World has its own service container for managing World-level global services.
 *
 * @zh 服务容器层级：
 * - Core.services: 应用程序全局服务
 * - World.services: World级别服务（每个World独立）
 * - Scene.services: Scene级别服务（每个Scene独立）
 * @en Service container hierarchy:
 * - Core.services: Application-wide global services
 * - World.services: World-level services (independent per World)
 * - Scene.services: Scene-level services (independent per Scene)
 *
 * @example
 * ```typescript
 * const roomWorld = new World({ name: 'Room_001' });
 * roomWorld.services.registerSingleton(RoomManager);
 *
 * const gameScene = roomWorld.createScene('game');
 * roomWorld.setSceneActive('game', true);
 * roomWorld.start();
 * ```
 */
export class World {
    constructor(config = {}) {
        this._scenes = new Map();
        this._activeScenes = new Set();
        this._globalSystems = [];
        this._isActive = false;
        this._config = { ...DEFAULT_CONFIG, ...config };
        this.name = this._config.name;
        this._createdAt = Date.now();
        this._services = new ServiceContainer();
    }
    /**
     * @zh World级别的服务容器
     * @en World-level service container
     */
    get services() {
        return this._services;
    }
    /**
     * @zh 检查World是否激活
     * @en Check if World is active
     */
    get isActive() {
        return this._isActive;
    }
    /**
     * @zh 获取Scene数量
     * @en Get scene count
     */
    get sceneCount() {
        return this._scenes.size;
    }
    /**
     * @zh 获取创建时间
     * @en Get creation time
     */
    get createdAt() {
        return this._createdAt;
    }
    /**
     * @zh 创建并添加Scene到World
     * @en Create and add Scene to World
     *
     * @param sceneName - @zh Scene名称 @en Scene name
     * @param sceneInstance - @zh Scene实例（可选）@en Scene instance (optional)
     * @returns @zh 创建的Scene实例 @en Created Scene instance
     * @throws @zh 名称为空、重复或超出限制时抛出错误 @en Throws if name is empty, duplicate, or limit exceeded
     */
    createScene(sceneName, sceneInstance) {
        this.validateSceneName(sceneName);
        const scene = sceneInstance ?? new Scene();
        if (this._config.debug) {
            const monitor = new PerformanceMonitor();
            monitor.enable();
            scene.services.registerInstance(PerformanceMonitor, monitor);
        }
        scene.id = sceneName;
        scene.name || (scene.name = sceneName);
        this._scenes.set(sceneName, scene);
        scene.initialize();
        return scene;
    }
    /**
     * @zh 移除Scene
     * @en Remove Scene
     *
     * @param sceneName - @zh Scene名称 @en Scene name
     * @returns @zh 是否成功移除 @en Whether removal was successful
     */
    removeScene(sceneName) {
        const scene = this._scenes.get(sceneName);
        if (!scene)
            return false;
        if (this._activeScenes.has(sceneName)) {
            this.setSceneActive(sceneName, false);
        }
        scene.end();
        this._scenes.delete(sceneName);
        logger.info(`从World '${this.name}' 中移除Scene: ${sceneName}`);
        return true;
    }
    /**
     * @zh 移除所有Scene
     * @en Remove all Scenes
     */
    removeAllScenes() {
        this._scenes.forEach((_, name) => this.removeScene(name));
        logger.info(`从World '${this.name}' 中移除所有Scene`);
    }
    /**
     * @zh 获取Scene
     * @en Get Scene
     *
     * @param sceneName - @zh Scene名称 @en Scene name
     * @returns @zh Scene实例或null @en Scene instance or null
     */
    getScene(sceneName) {
        return this._scenes.get(sceneName) ?? null;
    }
    /**
     * @zh 获取所有Scene ID
     * @en Get all Scene IDs
     */
    getSceneIds() {
        return Array.from(this._scenes.keys());
    }
    /**
     * @zh 获取所有Scene
     * @en Get all Scenes
     */
    getAllScenes() {
        return Array.from(this._scenes.values());
    }
    /**
     * @zh 设置Scene激活状态
     * @en Set Scene active state
     *
     * @param sceneName - @zh Scene名称 @en Scene name
     * @param active - @zh 是否激活 @en Whether to activate
     */
    setSceneActive(sceneName, active) {
        const scene = this._scenes.get(sceneName);
        if (!scene) {
            logger.warn(`Scene '${sceneName}' 不存在于World '${this.name}' 中`);
            return;
        }
        if (active) {
            this._activeScenes.add(sceneName);
            scene.begin?.();
            logger.debug(`在World '${this.name}' 中激活Scene: ${sceneName}`);
        }
        else {
            this._activeScenes.delete(sceneName);
            logger.debug(`在World '${this.name}' 中停用Scene: ${sceneName}`);
        }
    }
    /**
     * @zh 检查Scene是否激活
     * @en Check if Scene is active
     */
    isSceneActive(sceneName) {
        return this._activeScenes.has(sceneName);
    }
    /**
     * @zh 获取活跃Scene数量
     * @en Get active Scene count
     */
    getActiveSceneCount() {
        return this._activeScenes.size;
    }
    /**
     * @zh 添加全局System
     * @en Add global System
     *
     * @param system - @zh 全局System实例 @en Global System instance
     * @returns @zh 添加的System实例 @en Added System instance
     */
    addGlobalSystem(system) {
        if (this._globalSystems.includes(system)) {
            return system;
        }
        this._globalSystems.push(system);
        system.initialize?.();
        logger.debug(`在World '${this.name}' 中添加全局System: ${system.name}`);
        return system;
    }
    /**
     * @zh 移除全局System
     * @en Remove global System
     *
     * @param system - @zh 要移除的System @en System to remove
     * @returns @zh 是否成功移除 @en Whether removal was successful
     */
    removeGlobalSystem(system) {
        const index = this._globalSystems.indexOf(system);
        if (index === -1)
            return false;
        this._globalSystems.splice(index, 1);
        system.reset?.();
        logger.debug(`从World '${this.name}' 中移除全局System: ${system.name}`);
        return true;
    }
    /**
     * @zh 获取全局System
     * @en Get global System
     *
     * @param type - @zh System类型 @en System type
     * @returns @zh System实例或null @en System instance or null
     */
    getGlobalSystem(type) {
        return this._globalSystems.find(s => s instanceof type) ?? null;
    }
    /**
     * @zh 启动World
     * @en Start World
     */
    start() {
        if (this._isActive)
            return;
        this._isActive = true;
        this._globalSystems.forEach(s => s.initialize?.());
        logger.info(`启动World: ${this.name}`);
    }
    /**
     * @zh 停止World
     * @en Stop World
     */
    stop() {
        if (!this._isActive)
            return;
        this._activeScenes.forEach(name => this.setSceneActive(name, false));
        this._globalSystems.forEach(s => s.reset?.());
        this._isActive = false;
        logger.info(`停止World: ${this.name}`);
    }
    /**
     * @zh 销毁World
     * @en Destroy World
     */
    destroy() {
        logger.info(`销毁World: ${this.name}`);
        this.stop();
        this.removeAllScenes();
        this._globalSystems.forEach(s => s.destroy?.() ?? s.reset?.());
        this._globalSystems.length = 0;
        this._services.clear();
        this._scenes.clear();
        this._activeScenes.clear();
    }
    /**
     * @zh 更新World中的全局System
     * @en Update global systems in World
     * @internal
     */
    updateGlobalSystems() {
        if (!this._isActive)
            return;
        this._globalSystems.forEach(s => s.update?.());
    }
    /**
     * @zh 更新World中的所有激活Scene
     * @en Update all active scenes in World
     * @internal
     */
    updateScenes() {
        if (!this._isActive)
            return;
        this._activeScenes.forEach(name => {
            this._scenes.get(name)?.update?.();
        });
        if (this._config.autoCleanup) {
            this.cleanup();
        }
    }
    /**
     * @zh 获取World状态
     * @en Get World status
     */
    getStatus() {
        const scenes = [];
        this._scenes.forEach((scene, id) => {
            scenes.push({
                id,
                name: scene.name || id,
                isActive: this._activeScenes.has(id)
            });
        });
        return {
            name: this.name,
            isActive: this._isActive,
            sceneCount: this._scenes.size,
            activeSceneCount: this._activeScenes.size,
            globalSystemCount: this._globalSystems.length,
            createdAt: this._createdAt,
            config: { ...this._config },
            scenes
        };
    }
    /**
     * @zh 获取World统计信息
     * @en Get World statistics
     */
    getStats() {
        let totalEntities = 0;
        let totalSystems = this._globalSystems.length;
        this._scenes.forEach(scene => {
            totalEntities += scene.entities?.count ?? 0;
            totalSystems += scene.systems?.length ?? 0;
        });
        return {
            totalEntities,
            totalSystems,
            memoryUsage: 0,
            performance: {
                averageUpdateTime: 0,
                maxUpdateTime: 0
            }
        };
    }
    /**
     * @zh 验证Scene名称
     * @en Validate Scene name
     */
    validateSceneName(sceneName) {
        if (!sceneName?.trim()) {
            throw new Error('Scene name不能为空');
        }
        if (this._scenes.has(sceneName)) {
            throw new Error(`Scene name '${sceneName}' 已存在于World '${this.name}' 中`);
        }
        if (this._scenes.size >= this._config.maxScenes) {
            throw new Error(`World '${this.name}' 已达到最大Scene数量限制: ${this._config.maxScenes}`);
        }
    }
    /**
     * @zh 检查Scene是否可以被自动清理
     * @en Check if a scene is eligible for auto cleanup
     */
    isCleanupCandidate(sceneName, scene) {
        const elapsed = Date.now() - this._createdAt;
        return !this._activeScenes.has(sceneName) &&
            scene.entities != null &&
            scene.entities.count === 0 &&
            elapsed > this._config.cleanupThresholdMs;
    }
    /**
     * @zh 执行自动清理操作
     * @en Execute auto cleanup operation
     */
    cleanup() {
        const toRemove = [];
        this._scenes.forEach((scene, name) => {
            if (this.isCleanupCandidate(name, scene)) {
                toRemove.push(name);
            }
        });
        toRemove.forEach(name => {
            this.removeScene(name);
            logger.debug(`自动清理空Scene: ${name} from World ${this.name}`);
        });
    }
}
//# sourceMappingURL=World.js.map